/*
 * PasswordInput: password with a show/hide toggle and, with `strength`, a four-step strength meter scored
 * locally (length, character classes, common patterns). Joins a surrounding <Field>.
 *   <PasswordInput autoComplete="new-password" strength {...register('password')} />
 */
import { Eye, EyeSlash } from '@phosphor-icons/react'
import { forwardRef, useState, type InputHTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'
import { Input } from './Input'

/** 0 (empty) to 4 (strong). */
export function passwordScore(pw: string): 0 | 1 | 2 | 3 | 4 {
  if (!pw) return 0
  let s = 0
  if (pw.length >= 10) s++
  if (pw.length >= 14) s++
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length
  if (classes >= 2) s++
  if (classes >= 3) s++
  if (/^(.)\1+$/.test(pw) || /^(?:password|12345|qwerty|bpanel)/i.test(pw)) s = Math.min(s, 1)
  if (pw.length < 10) s = Math.min(s, 1)
  return Math.max(1, Math.min(4, s)) as 1 | 2 | 3 | 4
}

const LEVELS = ['weak', 'weak', 'fair', 'good', 'strong'] as const
const TONE = ['bg-control-strong', 'bg-danger-600', 'bg-warm-500', 'bg-brand-500', 'bg-teal-600']

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { strength?: boolean }

export const PasswordInput = forwardRef<HTMLInputElement, Props>(function PasswordInput(
  { strength, className, onChange, value, defaultValue, ...props },
  ref,
) {
  const { t } = useTranslation('common')
  const [visible, setVisible] = useState(false)
  const [internal, setInternal] = useState(String(defaultValue ?? ''))
  const current = value !== undefined ? String(value) : internal
  const score = passwordScore(current)
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Input
        ref={ref}
        type={visible ? 'text' : 'password'}
        ltr
        value={value}
        defaultValue={defaultValue}
        onChange={(e) => {
          setInternal(e.target.value)
          onChange?.(e)
        }}
        end={
          <button
            type="button"
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? t('ui.password.hide') : t('ui.password.show')}
            aria-pressed={visible}
            className="inline-flex size-8 items-center justify-center rounded-lg text-subtle transition-colors hover:bg-control hover:text-ink touch:size-10"
          >
            {visible ? <EyeSlash aria-hidden="true" /> : <Eye aria-hidden="true" />}
          </button>
        }
        {...props}
      />
      {strength ? (
        <div className="flex items-center gap-3" aria-live="polite">
          <div className="grid flex-1 grid-cols-4 gap-1" aria-hidden="true">
            {[1, 2, 3, 4].map((i) => (
              <span
                key={i}
                className={cn(
                  'h-1 rounded-full transition-colors duration-300',
                  i <= score ? TONE[score] : 'bg-control-strong',
                )}
              />
            ))}
          </div>
          <span className="min-w-14 text-end text-12 font-medium text-muted">
            {score ? t(`ui.password.strength.${LEVELS[score]}`) : t('ui.password.strength.empty')}
          </span>
        </div>
      ) : null}
    </div>
  )
})
